import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  CONTENT_TYPE_CSV,
  CONTENT_TYPE_CSV_ANDROID,
} from '@shared/constants/files';
import { SentenceCasePipe } from '@shared/pipes/sentence-case.pipe';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import { ProductPropertiesEnum } from '@usf/product-types/Product-Summary';
import { FileHandlerBaseService } from '@shared/services/file-handler/file-handler-base.service';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { AcronymTitleCasePipe } from '@shared/pipes/acronym-title-case.pipe';
import { ToastService } from '@shared/services/toast/toast.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { PlatformService } from '@panamax/app-state';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { MerchPrintModalComponent } from '@shared/components/merch-print-modal/merch-print-modal.component';
import { ProductAnalyticsService } from '@usf/ngrx-product';

@Injectable({
  providedIn: 'root',
})
export class ProductCompareDownloadService extends FileHandlerBaseService {
  readonly propertiesEnum = ProductPropertiesEnum;
  fileNamePrefix = 'ProductCompare_Cust'; // ProductCompare_Cust{customer number}_{mm/dd/yyyy}_{time}
  private readonly defaultNutritionLength = 17;
  constructor(
    toastService: ToastService,
    public translateService: TranslateService,
    public platform: Platform,
    public datePipe: DatePipe,
    public sentenceCasePipe: SentenceCasePipe,
    public loadingSpinnerService: LoadingSpinnerService,
    private modalController: ModalController,
    private modalService: ModalService,
    private platformService: PlatformService,
    private analyticsService: ProductAnalyticsService,
    public acronymTitleCasePipe: AcronymTitleCasePipe,
  ) {
    super(
      toastService,
      translateService,
      platform,
      datePipe,
      sentenceCasePipe,
      loadingSpinnerService,
    );
  }

  labelsMap = {
    headers: [
      'Product Number',
      'Product Description',
      'Brand Name',
      'Case Price',
      'Each Price',
      'Pack Size',
      'Price Per Portion',
      'Case Available Inventory',
      'Each Available Inventory',
      'Product Attributes',
      'Purchase History',
      'Net Weight',
      'USF Storage',
      'Ingredients',
      'Serving Size',
      'Serving Per Container',
      'Calories',
      'Total Fat',
      'Saturated Fat',
      'Trans Fat',
      'Cholesterol',
      'Sodium',
      'Total Carbohydrate',
      'Dietary Fiber',
      'Total Sugar',
      'Added Sugars',
      'Protein',
      'Vitamin D',
      'Calcium',
      'Iron',
      'Potassium',
    ],
    nutrientMap: {
      'Total Fat': 3,
      'Saturated Fat': 4,
      'Trans Fat': 5,
      'Cholesterol': 6,
      'Sodium': 7,
      'Total Carbohydrate': 8,
      'Dietary Fiber': 9,
      'Total Sugar': 10,
      'Added Sugars': 11,
      'Protein': 12,
      'Vitamin D': 13,
      'Calcium': 14,
      'Iron': 15,
      'Potassium': 16,
    },
  };

  labelsMapUsda = {
    headers: [
      'Product Number',
      'Product Description',
      'Brand Name',
      'Case Price',
      'Each Price',
      'Pack Size',
      'Price Per Portion',
      'Case Available Inventory',
      'Each Available Inventory',
      'Product Attributes',
      'Purchase History',
      'Net Weight',
      'USF Storage',
      'Ingredients',
      'Serving Size',
      'Serving Per Container',
      'Calories',
      'Calories from Fat',
      'Total Fat',
      'Saturated Fat',
      'Trans Fat',
      'Cholesterol',
      'Sodium',
      'Total Carbohydrate',
      'Dietary Fiber',
      'Total Sugar',
      'Protein',
      'Vitamin A',
      'Vitamin C',
      'Calcium',
      'Iron',
    ],
    nutrientMap: {
      'Total Fat': 4,
      'Saturated Fat': 5,
      'Trans Fat': 6,
      'Cholesterol': 7,
      'Sodium': 8,
      'Total Carbohydrate': 9,
      'Dietary Fiber': 10,
      'Total Sugar': 11,
      'Protein': 12,
      'Vitamin A': 13,
      'Vitamin C': 14,
      'Calcium': 15,
      'Iron': 16,
    },
  };

  labelsMapMerged = {
    headers: [
      'Product Number',
      'Product Description',
      'Brand Name',
      'Case Price',
      'Each Price',
      'Pack Size',
      'Price Per Portion',
      'Case Available Inventory',
      'Each Available Inventory',
      'Product Attributes',
      'Purchase History',
      'Net Weight',
      'USF Storage',
      'Ingredients',
      'Serving Size',
      'Serving Per Container',
      'Calories',
      'Calories from Fat',
      'Total Fat',
      'Saturated Fat',
      'Trans Fat',
      'Cholesterol',
      'Sodium',
      'Total Carbohydrate',
      'Dietary Fiber',
      'Total Sugar',
      'Added Sugars',
      'Protein',
      'Vitamin A',
      'Vitamin C',
      'Vitamin D',
      'Calcium',
      'Iron',
      'Potassium',
    ],
    nutrientMap: {
      'Total Fat': 4,
      'Saturated Fat': 5,
      'Trans Fat': 6,
      'Cholesterol': 7,
      'Sodium': 8,
      'Total Carbohydrate': 9,
      'Dietary Fiber': 10,
      'Total Sugar': 11,
      'Added Sugars': 12,
      'Protein': 13,
      'Vitamin A': 14,
      'Vitamin C': 15,
      'Vitamin D': 16,
      'Calcium': 17,
      'Iron': 18,
      'Potassium': 19,
    },
  };

  async showDownloadModal(products: ProductDetailViewModel[]) {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ModalWithNavComponent,
      {
        rootPage: MerchPrintModalComponent,
        rootPageParams: {
          title: 'i18n.search.searchCompare.downloadModalTitle',
          confirmButton: 'i18n.common.download',
        },
      },
      'modal-desktop-size-sm',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();

    await modal.onDidDismiss().then((resp: any) => {
      if (!!resp?.data && resp?.data?.includeProductPrices !== undefined) {
        this.downloadProductCompare(products, resp?.data?.includeProductPrices);
      }
    });
  }

  async downloadProductCompare(
    productDetails: ProductDetailViewModel[],
    includeProductPrices: boolean,
  ): Promise<void> {
    await this.loadingSpinnerService
      .createSpinnerModal()
      .then(async () => {
        const fileNameWithExtension = this.buildFileName(
          productDetails[0]?.selectedCustomer?.customerNumber,
        );
        let contentTypeString = this.platform.is('android')
          ? CONTENT_TYPE_CSV_ANDROID
          : CONTENT_TYPE_CSV;
        let utf8 = true;
        const fileData = await this.generateCsvBlob(
          productDetails,
          includeProductPrices,
        );
        this.downloadFile(
          fileData,
          fileNameWithExtension,
          contentTypeString,
          this.platform.is('android'),
          utf8,
        );
        this.analyticsService.trackDownloadProductComparison();
      })
      .finally(() => {
        this.cleanUp();
      });
  }

  private buildFileName(customerNumber: number) {
    return [
      this.fileNamePrefix,
      customerNumber,
      '_',
      this.datePipe.transform(new Date(), 'MMddyyyy'),
      '_',
      this.datePipe.transform(new Date(), 'HHmmss'),
      '.csv',
    ].join('');
  }

  async generateCsvBlob(
    productDetails: ProductDetailViewModel[],
    includeProductPrices: boolean,
  ) {
    const csvContent = await this.generateCsvContent(
      productDetails,
      includeProductPrices,
    );
    return new Blob([csvContent], { type: 'text/csv' });
  }

  async generateCsvContent(
    productDetails: ProductDetailViewModel[],
    includeProductPrices: boolean,
  ) {
    const labelsMap = productDetails.some(product => product?.isUsda)
      ? productDetails.every(product => product?.isUsda)
        ? this.labelsMapUsda
        : this.labelsMapMerged
      : this.labelsMap;
    const rows = productDetails.map(product => {
      const casePrice = includeProductPrices
        ? this.getPrice(product, 'casePrice')
        : 'N/A';
      const eachPrice = includeProductPrices
        ? this.getPrice(product, 'eachPrice')
        : 'N/A';
      const salesPackSize = product?.summary?.salesPackSize || 'N/A';
      const pricePerPortion = includeProductPrices
        ? this.getTextFromDOMById(`pricePerPortion-${product?.productNumber}`)
        : 'N/A';
      const invCases = this.getTextFromDOMById(
        `casesInventory-${product?.productNumber}`,
      );
      const invEaches = this.getTextFromDOMById(
        `eachesInventory-${product?.productNumber}`,
      );
      const productAttributes = this.getProductAttributes(
        product?.productNumber,
      );
      const purchaseHistory = this.formatMultilineElement(
        product?.productNumber,
        'purchaseHistory',
      );
      const netWeight = this.getTextFromDOMById(
        `netWeight-${product?.productNumber}`,
      );
      const usfStorage = this.getTextFromDOMById(
        `usfStorage-${product?.productNumber}`,
      );
      const ingredients = product.detail?.ingredients || 'N/A';
      const nutritionFacts = this.getNutritionFacts(
        product,
        labelsMap.nutrientMap,
      );
      return [
        product.productNumber,
        this.acronymTitleCasePipe.transform(
          this.escapeCsvField(product.summary?.productDescTxtl),
        ),
        this.acronymTitleCasePipe.transform(
          this.escapeCsvField(product.summary?.brand),
        ),
        casePrice,
        eachPrice,
        salesPackSize,
        pricePerPortion,
        invCases,
        invEaches,
        this.escapeCsvField(productAttributes),
        this.escapeCsvField(purchaseHistory),
        netWeight,
        this.escapeCsvField(usfStorage),
        this.escapeCsvField(ingredients),
        ...nutritionFacts,
      ];
    });
    return [
      labelsMap.headers.join(','),
      ...rows.map(row => (Array.isArray(row) ? row.join(',') : row)),
    ].join('\r\n');
  }

  private getPrice(product: ProductDetailViewModel, priceType: string): string {
    const specialOrder = product?.summary?.properties?.has(
      this.propertiesEnum.specialOrder,
    );
    return this.getTextFromDOMById(
      `${specialOrder ? 'specialPrice' : priceType}-${product?.productNumber}`,
    );
  }

  private formatMultilineElement(
    productNumber: number,
    elementPrefix: string,
  ): string | null {
    let elementInnerText = this.getTextFromDOMById(
      `${elementPrefix}-${productNumber}`,
    );
    if (elementInnerText) {
      elementInnerText = elementInnerText.includes('\n')
        ? elementInnerText.replace(/\n/g, ',').replace(/,$/, '')
        : elementInnerText;
    }
    return elementInnerText;
  }

  private getProductAttributes(productNumber: number): string {
    return this.getChipsTextFromDOMById(
      `productAttributes-${productNumber}`,
    )?.join(',');
  }

  private getNutritionFacts(
    product: ProductDetailViewModel,
    nutrientMap: any,
  ): string[] {
    const defaultNutrition = Array(this.defaultNutritionLength).fill('N/A');
    if (!product.nutritionFacts?.nutrientIsNotAvailable) {
      const { servingSizeData, calories, nutrientsData, caloriesFromFat } =
        product.nutritionFacts;
      defaultNutrition[0] = this.escapeCsvField(servingSizeData) || 'N/A';
      defaultNutrition[1] =
        product?.detail?.servingsPerCase !== undefined
          ? product.detail.servingsPerCase
          : 'N/A';
      defaultNutrition[2] = calories !== undefined ? `${calories}` : 'N/A';
      defaultNutrition[3] =
        product.isUsda && caloriesFromFat !== undefined
          ? `${caloriesFromFat}`
          : 'N/A';

      if (nutrientsData) {
        nutrientsData.forEach(nutrient => {
          const nutrientValue = this.getTextFromDOMById(
            `nutrientsData-${nutrient.data?.nutrientName}-${product?.productNumber}`,
          );
          const index = this.getNutrientIndex(
            nutrient.data?.nutrientName,
            nutrientMap,
          );
          if (index !== -1) {
            defaultNutrition[index] =
              this.getNutrientValueAfterColon(nutrientValue);
          }
        });
      }
    }
    return defaultNutrition;
  }

  private getNutrientIndex(nutrientName: string, nutrientMap): number {
    return nutrientMap[nutrientName] || -1;
  }

  private getNutrientValueAfterColon(nutrientValue: String): string {
    if (nutrientValue === null || nutrientValue === undefined) {
      return 'N/A';
    }
    const parts = nutrientValue.split(':');
    return parts.length < 2 ? 'N/A' : parts[1].trim();
  }

  private getTextFromDOMById = (id: string) => {
    return document.getElementById(id)?.innerText || 'N/A';
  };
  private getChipsTextFromDOMById = (id: string) => {
    const chips = document.getElementById(id)?.querySelectorAll('app-usf-chip');
    if (!chips) return [];
    return Array.from(chips).map(chip => {
      const element = chip as HTMLElement;
      return (
        element.innerText ||
        element.querySelector('ion-icon')?.getAttribute('alt') ||
        element.querySelector('img')?.getAttribute('alt') ||
        'N/A'
      );
    });
  };

  private escapeCsvField = (field: any): string => {
    if (field === null || field === undefined) {
      return 'N/A';
    }
    const fieldStr = field.toString();
    if (fieldStr.includes(',')) {
      return `"${fieldStr.replace(/"/g, '""')}"`;
    }
    return fieldStr;
  };
}
